/** @format */

import icon_moreinfo from '@/assets/icons/dovevivo_bookingcard_moreinfo.svg';
import icon_telephone from '@/assets/icons/dovevivo_bookingcard_telephone.svg';
import icon_calendar from '@/assets/joivy_icon_calendar_black.svg';
import icon_whatsapp from '@/assets/joivy_icon_whatsapp_black.svg';
import {
  selectorBookingAvailability,
  selectorIsNotSalableOnline,
  selectorIsRoomNotAvailable,
} from '@/ducks/bookingNew/selectors';
import { selectorBookVisitFormID } from '@/ducks/general/selectors';
import {
  GTMAskInfoS1,
  GTMbookVisit,
  GTMPhonecallCTA,
  GTMWhatsAppCta,
} from '@/ducks/googleTagManager/helpers/google-tag-manager';
import { TRoom } from '@/ducks/room/declarations';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { calcWhatsappNumber } from '@/utils/helpers/calcWhatsappNumber';
import { utilityGetPrice } from '@/utils/helpers/utilityGetPrice';
import { utilityGetVisitURL } from '@/utils/helpers/utilityGetVisitURL';
import { zendeskRequestInfoUrlGenerator } from '@/utils/helpers/zendeskLinksGenerator';
import { useIntl } from 'gatsby-plugin-intl';
import moment from 'moment';
import { nanoid } from 'nanoid';
import React, { FC, useEffect, useMemo } from 'react';
import { ContactBookingContainer, ContainerButtons, H4, StyledLink, WorkingHours } from './ContactsBooking.style';

export const ContactsBooking: FC<ContactsBookingProps> = ({ room, isMobile = false }) => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const availability = useAppSelector(selectorBookingAvailability);
  const isRoomNotSalableOnline = useAppSelector(selectorIsNotSalableOnline);
  const isRoomNotAvailable = useAppSelector(selectorIsRoomNotAvailable);
  const bookVisitFormID = useAppSelector(selectorBookVisitFormID);

  const [requestVisitDisabled, setRequestVisitDisabled] = React.useState(false);

  const { getFormattedPrice } = utilityGetPrice({ room, availability });

  const requestVisitUrl = utilityGetVisitURL({
    roomCode: room.code,
    formatMessage,
  });

  useEffect(() => {
    if (availability) {
      setRequestVisitDisabled(moment(availability.availableFrom) > moment().add(2, 'months'));
    }
  }, [availability]);

  useEffect(() => {
    dispatch({
      type: 'SET_BOOK_VISIT_FORMID',
      payload: nanoid(),
    });
  }, []);

  const handleClickRequestMoreInfo = () => {
    GTMAskInfoS1({ monthlyRate: getFormattedPrice(), roomCode: room.code });
  };

  const handleClickPhoneNumber = () => {
    GTMPhonecallCTA({ roomCode: room.code, monthlyRate: getFormattedPrice() });
  };

  const isPortugueseCountryCode = room.countryCode === 'PRT' || room.countryCode === 'PT';
  const isSpanishCountryCode = room.countryCode === 'ESP' || room.countryCode === 'ES';
  const isFrenchCountryCode = room.countryCode === 'FRA' || room.countryCode === 'FR';
  const isMilanCity = room.city.nameEn.toLowerCase() === 'milan';

  const whatsappNumber = useMemo(() => calcWhatsappNumber(room.countryCode), [room.countryCode]);

  const showRequestVisitButton = useMemo(() => {
    if (isPortugueseCountryCode) return false;
    if (isFrenchCountryCode || isSpanishCountryCode) return false;
    if (requestVisitDisabled) return false;
    if (isRoomNotSalableOnline) return false;
    if (isRoomNotAvailable) return false;
    return true;
  }, [
    isFrenchCountryCode,
    isSpanishCountryCode,
    isPortugueseCountryCode,
    isRoomNotAvailable,
    isRoomNotSalableOnline,
    requestVisitDisabled,
  ]);

  const showRequestInfoButton = useMemo(() => {
    if (isPortugueseCountryCode) return false;
    return !isRoomNotSalableOnline || isRoomNotAvailable;
  }, [isRoomNotAvailable, isRoomNotSalableOnline, isPortugueseCountryCode]);

  const requestInfoHref = useMemo(() => {
    if (isFrenchCountryCode) return 'mailto:coliving.france@joivy.com';
    return zendeskRequestInfoUrlGenerator({
      city: room.city.name,
      roomCode: room.code,
      price: getFormattedPrice(),
      countryCode: room.countryCode,
    });
  }, [isFrenchCountryCode, room]);

  const phoneNumber = useMemo(() => {
    //THIS IS COMMENTED OUT WHILE WE DON'T HAVE THE NEW PHONE NUMEBR FOR MILAN;
    // if (isMilanCity) return { label: '+39 02 8718 9774', href: 'tel:+390287189774' };
    if (isFrenchCountryCode) return { label: '+33 09 72 32 31 02', href: 'tel:+330972323102' };
    return false;
  }, [isMilanCity, isFrenchCountryCode]);

  return (
    <ContactBookingContainer>
      <H4>{formatMessage({ id: 'still_doubts' })}</H4>
      <ContainerButtons $mobile={isMobile}>
        {showRequestVisitButton && (
          <StyledLink
            to={requestVisitUrl}
            onClick={() => GTMbookVisit(getFormattedPrice(), room.code, 'video_call', bookVisitFormID)}
            rel="noreferrer nofollow"
          >
            <img role="presentation" src={icon_calendar} width="24" />
            {formatMessage({ id: 'request_visit' })}
          </StyledLink>
        )}
        {whatsappNumber && (
          <StyledLink
            as="a"
            aria-label={formatMessage({ id: 'chat_on_whatsapp' })}
            target="_blank"
            rel="noopener noreferrer"
            href={`https://wa.me/${whatsappNumber}`}
            onClick={() => GTMWhatsAppCta(room.code, getFormattedPrice())}
          >
            <img role="presentation" src={icon_whatsapp} width="24" />
            {formatMessage({ id: 'chat_on_whatsapp' })}
          </StyledLink>
        )}
        {showRequestInfoButton && (
          <StyledLink as="a" onClick={handleClickRequestMoreInfo} rel="noreferrer nofollow" href={requestInfoHref}>
            <img role="presentation" src={icon_moreinfo} width="24" />
            {formatMessage({ id: 'request_more_info' })}
          </StyledLink>
        )}
        {!!phoneNumber && (
          <div>
            <StyledLink as="a" onClick={handleClickPhoneNumber} rel="noreferrer nofollow" href={phoneNumber.href}>
              <img role="presentation" src={icon_telephone} width="24" />
              {phoneNumber.label}
            </StyledLink>
            {isMilanCity && <WorkingHours>{formatMessage({ id: 'pdp.phone_working_hours_milan' })}</WorkingHours>}
          </div>
        )}
      </ContainerButtons>
    </ContactBookingContainer>
  );
};

type ContactsBookingProps = {
  room: TRoom;
  isMobile?: boolean;
};
